import { useEffect, useState } from 'react'

const COMPACT_ENTER_PX = 80
const COMPACT_EXIT_PX = 40

/**
 * Tracks whether the user has scrolled past the expanded sticky-header height
 * (so the page can collapse it to a single compact row). Hysteresis (enter at
 * 80, exit at 40) prevents flicker right at the threshold when the layout
 * height changes between the expanded/compact states.
 *
 * Scroll is observed on `window` because the real scroll container in this app
 * is `<body>` (see `index.scss`); body scroll bubbles to the window scroll
 * event so a single listener catches both. We use `requestAnimationFrame`
 * throttling so scroll-heavy pages don't trigger a state update on every wheel
 * tick.
 *
 * Shared by the UA Insights game page and the Compare page so both collapse
 * their sticky headers identically.
 */
export function useStickyHeader(): boolean {
  const [isCompactHeader, setIsCompactHeader] = useState(false)

  useEffect(() => {
    const scroller = document.scrollingElement ?? document.documentElement
    let ticking = false
    const onScroll = () => {
      if (ticking) return
      ticking = true
      requestAnimationFrame(() => {
        const top = scroller?.scrollTop ?? 0
        setIsCompactHeader((prev) => {
          if (!prev && top > COMPACT_ENTER_PX) return true
          if (prev && top < COMPACT_EXIT_PX) return false
          return prev
        })
        ticking = false
      })
    }
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  return isCompactHeader
}
